import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import ConfirmModal from '../components/ConfirmModal';
import PlaceSearch from '../components/PlaceSearch';
import { formatRoles } from '../utils/roleUtils';
import GoogleMap from '../components/GoogleMap';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import AmbulanceStaffMapping, { Ambulance } from '../components/AmbulanceStaffMapping';
import AmbulanceAssignmentHistoryContent from '../components/AmbulanceAssignmentHistoryContent';
import AreaMapping from '../components/AreaMapping';
import { formatDateDDMMYYYY } from '../utils/dateUtils';
import {
  getAmbulances,
  createAmbulance,
  updateAmbulance,
  deleteAmbulance,
  getAmbulanceTypes,
  getStaffCategories,
  getUsers,
  getGpsDevices,
  getGpsDevice,
  getAreas,
  updateArea,
  uploadFile,
  getTenant,
  User,
  GpsDevice,
  StaffCategory,
  AmbulanceType,
  Area,
} from '../services/api';

interface AmbulanceManagementProps {
  skipLayout?: boolean;
  hideAmbulanceTab?: boolean;
}

const AmbulanceManagement: React.FC<AmbulanceManagementProps> = ({ skipLayout = false, hideAmbulanceTab = false }) => {
  const { hasPermission, isSystemAdmin, isTenantAdmin, user } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [ambulances, setAmbulances] = useState<Ambulance[]>([]);
  const [allAmbulances, setAllAmbulances] = useState<Ambulance[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingAmbulance, setViewingAmbulance] = useState<Ambulance | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [ambulanceToDelete, setAmbulanceToDelete] = useState<{ id: string; name: string } | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [editingAmbulance, setEditingAmbulance] = useState<Ambulance | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    licensePlate: '',
    vehicleNumber: '',
    type: '',
    status: 'available',
    capacity: '',
    mileage: '',
    speedLimit: '',
    currentLocation: null as { latitude: number; longitude: number; address?: string } | null,
    assignedPilot: '',
    notes: '',
    gpsDeviceId: '',
    assignedStaffs: [] as string[],
    image: '',
  });
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{
    name?: string;
    licensePlate?: string;
    type?: string;
    capacity?: string;
    speedLimit?: string;
    assignedPilot?: string;
    assignedStaffs?: string;
    gpsDeviceId?: string;
  }>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [ambulanceTypes, setAmbulanceTypes] = useState<AmbulanceType[]>([]);
  const [staffCategories, setStaffCategories] = useState<StaffCategory[]>([]);
  const [pilotUsers, setPilotUsers] = useState<User[]>([]);
  const [staffUsers, setStaffUsers] = useState<User[]>([]);
  const [gpsDevices, setGpsDevices] = useState<GpsDevice[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [areasLoading, setAreasLoading] = useState(false);
  const [hospitalLocation, setHospitalLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const canViewMapping = isSystemAdmin || isTenantAdmin || hasPermission('ambulance.mapping');
  const canViewAreaMapping = isSystemAdmin || isTenantAdmin || hasPermission('area.update');
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') as 'ambulances' | 'mapping' | 'areaMapping' | 'history' | null;
  
  const [activeTab, setActiveTab] = useState<'ambulances' | 'mapping' | 'areaMapping' | 'history'>(() => {
    if (tabFromUrl && ['ambulances', 'mapping', 'areaMapping', 'history'].includes(tabFromUrl)) {
      return tabFromUrl;
    }
    // When hideAmbulanceTab is true (used in Settings), show ambulances
    if (hideAmbulanceTab) {
      return 'ambulances';
    }
    // In standalone Ambulance Management page, default to mapping or first available tab
    // (Ambulances tab is hidden, so default to mapping/areaMapping/history)
    if (canViewMapping) return 'mapping';
    if (canViewAreaMapping) return 'areaMapping';
    return 'history';
  });

  // Sync activeTab with URL params (e.g., when navigating from breadcrumb)
  useEffect(() => {
    const currentTab = searchParams.get('tab') as 'ambulances' | 'mapping' | 'areaMapping' | 'history' | null;
    if (currentTab && ['ambulances', 'mapping', 'areaMapping', 'history'].includes(currentTab)) {
      if (currentTab !== activeTab) {
        setActiveTab(currentTab);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  // Update URL when tab changes (but only if it doesn't match current URL to avoid loops)
  useEffect(() => {
    if (hideAmbulanceTab) {
      // In Settings, don't update URL for tab changes
      return;
    }
    const currentTab = searchParams.get('tab');
    const shouldHaveTab = activeTab !== 'ambulances';
    const hasTab = !!currentTab;
    
    if (shouldHaveTab && currentTab !== activeTab) {
      setSearchParams({ tab: activeTab }, { replace: true });
    } else if (!shouldHaveTab && hasTab) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, hideAmbulanceTab]);

  // When hideAmbulanceTab is true, force activeTab to 'ambulances' to show table
  useEffect(() => {
    if (hideAmbulanceTab && activeTab !== 'ambulances') {
      setActiveTab('ambulances');
    }
  }, [hideAmbulanceTab, activeTab]);

  // Set default tab for standalone Ambulance Management page (when hideAmbulanceTab is false)
  useEffect(() => {
    if (!hideAmbulanceTab && !tabFromUrl && activeTab === 'mapping') {
      // If mapping is not available, switch to first available tab
      if (!canViewMapping) {
        if (canViewAreaMapping) {
          setActiveTab('areaMapping');
        } else {
          setActiveTab('history');
        }
      }
    }
  }, [hideAmbulanceTab, tabFromUrl, canViewMapping, canViewAreaMapping, activeTab]);

  // Reset to default tab if user doesn't have permission for mapping
  useEffect(() => {
    if (activeTab === 'mapping' && !canViewMapping) {
      // Switch to first available tab
      if (canViewAreaMapping) {
        setActiveTab('areaMapping');
      } else {
        setActiveTab('history');
      }
    }
    if (activeTab === 'areaMapping' && !canViewAreaMapping) {
      // Switch to mapping or history
      if (canViewMapping) {
        setActiveTab('mapping');
      } else {
        setActiveTab('history');
      }
    }
  }, [activeTab, canViewMapping, canViewAreaMapping]);
  const [staffSearchTerm, setStaffSearchTerm] = useState('');
  const [pilotSearchTerm, setPilotSearchTerm] = useState('');
  const [showPilotDropdown, setShowPilotDropdown] = useState(false);
  const pilotDropdownRef = useRef<HTMLDivElement>(null);
  const [showStaffDropdown, setShowStaffDropdown] = useState(false);
  const staffDropdownRef = useRef<HTMLDivElement>(null);
  const [staffTooltip, setStaffTooltip] = useState<{
    x: number;
    y: number;
    staffList: Array<{ name: string }>;
  } | null>(null);

  // Fetch hospital location from tenant settings
  useEffect(() => {
    const fetchHospitalLocation = async () => {
      if (!user?.tenantId || user?.isSystem) {
        setHospitalLocation(null);
        return;
      }

      try {
        const response = await getTenant(user.tenantId);
        if (response.data.success && response.data.data) {
          const tenant = response.data.data;
          const hospital = tenant.spec?.hospital;
          
          if (hospital) {
            // Check hospitalLocation first (preferred), then fallback to direct latitude/longitude
            const hospitalLat = hospital.hospitalLocation?.latitude ?? hospital.latitude;
            const hospitalLng = hospital.hospitalLocation?.longitude ?? hospital.longitude;
            
            if (hospitalLat !== undefined && hospitalLng !== undefined && 
                !isNaN(hospitalLat) && !isNaN(hospitalLng) &&
                hospitalLat >= -90 && hospitalLat <= 90 &&
                hospitalLng >= -180 && hospitalLng <= 180) {
              setHospitalLocation({ latitude: hospitalLat, longitude: hospitalLng });
              return;
            }
          }
        }
      } catch (error) {
        console.error('Error fetching hospital location:', error);
      }
      
      setHospitalLocation(null);
    };

    fetchHospitalLocation();
  }, [user?.tenantId, user?.isSystem]);

  // Initial load - fetch ambulances and types immediately on mount
  useEffect(() => {
    fetchAmbulances();
    fetchAllAmbulances();
    fetchAmbulanceTypes();
    fetchStaffCategories();
    fetchPilotUsers();
    fetchStaffUsers();
    fetchAvailableGpsDevices();
    fetchAreas();
    setIsInitialLoad(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close drawer with Escape key
  useEffect(() => {
    if (!showModal) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        const activeElement = document.activeElement as HTMLElement | null;
        if (activeElement && typeof activeElement.blur === 'function') {
          activeElement.blur();
        }
        setShowModal(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showModal]);

  // Close pilot dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pilotDropdownRef.current && !pilotDropdownRef.current.contains(event.target as Node)) {
        setShowPilotDropdown(false);
      }
      if (staffDropdownRef.current && !staffDropdownRef.current.contains(event.target as Node)) {
        setShowStaffDropdown(false);
      }
    };

    if (showPilotDropdown || showStaffDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPilotDropdown, showStaffDropdown]);

  const fetchAmbulanceTypes = async () => {
    try {
      const response = await getAmbulanceTypes();
      setAmbulanceTypes(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to load ambulance types:', err);
    }
  };

  const fetchStaffCategories = async () => {
    try {
      const response = await getStaffCategories(1, 100);
      setStaffCategories(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to load staff categories:', err);
    }
  };

  const fetchPilotUsers = async () => {
    try {
      const response = await getUsers(1, 500);
      const allUsers = response.data.data || [];
      const pilots = allUsers.filter(
        (u) => u.spec?.staffCategory && u.spec.staffCategory.toLowerCase() === 'pilot'
      );
      setPilotUsers(pilots);
    } catch (err) {
      console.error('Failed to load pilot users:', err);
    }
  };

  const fetchStaffUsers = async () => {
    try {
      const response = await getUsers(1, 500);
      const allUsers = response.data.data || [];
      // Filter out pilots/drivers - include all other staff
      const staff = allUsers.filter((u) => {
        const staffCategory = (u.spec?.staffCategory || '').toLowerCase();
        const roles = (u.spec?.roles || u.roles || []).map((r: string) => r.toLowerCase());
        const isPilot = 
          staffCategory === 'pilot' || 
          staffCategory === 'driver' ||
          roles.some((r) => r.includes('pilot') || r.includes('driver'));
        return !isPilot;
      });
      setStaffUsers(staff);
    } catch (err) {
      console.error('Failed to load staff users:', err);
    }
  };


  const fetchAvailableGpsDevices = async (currentAttachedDeviceId?: string) => {
    try {
      // Get all unattached devices for this tenant
      const response = await getGpsDevices(1, 500, true);
      let devices = response.data.data || [];

      // If editing and there is an attached device, ensure it is included in the options
      if (currentAttachedDeviceId) {
        try {
          const currentDeviceResponse = await getGpsDevice(currentAttachedDeviceId);
          const currentDevice = currentDeviceResponse.data.data;
          if (currentDevice && !devices.some((d) => d._id === currentDevice._id)) {
            devices = [currentDevice, ...devices];
          }
        } catch {
          // Ignore errors fetching current device; it may have been deleted
        }
      }

      setGpsDevices(devices);
    } catch (err) {
      console.error('Failed to load GPS devices:', err);
    }
  };

  const fetchAreas = async () => {
    try {
      setAreasLoading(true);
      const response = await getAreas(1, 500);
      setAreas(response.data.data || []);
    } catch (err) {
      console.error('Failed to load areas:', err);
    } finally {
      setAreasLoading(false);
    }
  };

  const fetchAllAmbulances = async () => {
    try {
      const response = await getAmbulances(1, 500);
      setAllAmbulances(response.data.data || []);
    } catch (err) {
      console.error('Failed to load all ambulances:', err);
    }
  };

  // Debounce search term - wait 500ms after user stops typing
  useEffect(() => {
    if (isInitialLoad) return; // Skip debounce on initial load

    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 800);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm, isInitialLoad]);

  // Fetch ambulances when debounced search term or status filter changes
  useEffect(() => {
    if (isInitialLoad) return; // Skip on initial load (already fetched)
    fetchAmbulances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, statusFilter]);

  const fetchAmbulances = async (pageOverride?: number, pageSizeOverride?: number) => {
    try {
      setLoading(true);
      const pageToUse = pageOverride ?? currentPage;
      const sizeToUse = pageSizeOverride ?? pageSize;
      const params: any = { page: pageToUse, pageSize: sizeToUse };
      if (debouncedSearchTerm.trim()) {
        params.search = debouncedSearchTerm.trim();
      }
      if (statusFilter) {
        params.status = statusFilter;
      }
      const response = await getAmbulances(params.page, params.pageSize, params.search, params.status);
      setAmbulances(response.data.data || []);
      const pagination = response.data.pagination;
      if (pagination) {
        setCurrentPage(pagination.currentPage || pageToUse);
        setPageSize(pagination.pageSize || sizeToUse);
        setTotalItems(pagination.totalItems || 0);
        setHasNextPage(pagination.hasNextPage || false);
        setHasPreviousPage(pagination.hasPreviousPage || false);
      }
    } catch (err: any) {
      setError('Failed to load ambulances');
      console.error('Failed to load ambulances:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
  };

  const handleRefresh = () => {
    fetchAmbulances();
  };

  const handlePageChange = (page: number) => {
    fetchAmbulances(page, pageSize);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    fetchAmbulances(1, size);
  };

  const handleOpenModal = async (ambulance?: Ambulance) => {
    // Refresh ambulance types when opening modal
    await fetchAmbulanceTypes();
    const currentGpsDeviceId = ambulance?.spec.gpsDevice?.gpsDeviceId;
    await fetchAvailableGpsDevices(currentGpsDeviceId);

    if (ambulance) {
      setEditingAmbulance(ambulance);
      setFormData({
        name: ambulance.metadata.name,
        licensePlate: ambulance.spec.licensePlate,
        vehicleNumber: ambulance.spec.vehicleNumber || '',
        type: ambulance.spec.type || '',
        status: ambulance.spec.status,
        capacity: ambulance.spec.capacity?.toString() || '',
        mileage: ambulance.spec.mileage?.toString() || '',
        speedLimit: ambulance.spec.speedLimit?.toString() || '',
        currentLocation: ambulance.spec.currentLocation || null,
        assignedPilot: ambulance.spec.assignedPilot?.userId || '',
        notes: ambulance.spec.notes || '',
        gpsDeviceId: ambulance.spec.gpsDevice?.gpsDeviceId || '',
        assignedStaffs: ambulance.spec.assignedStaffs?.map(s => s.userId) || [],
        image: (ambulance.spec as any).image || '',
      });
    } else {
      setEditingAmbulance(null);
      setFormData({
        name: '',
        licensePlate: '',
        vehicleNumber: '',
        type: '',
        status: 'available',
        capacity: '',
        mileage: '',
        speedLimit: '',
        currentLocation: null,
        assignedPilot: '',
        notes: '',
        gpsDeviceId: '',
        assignedStaffs: [],
        image: '',
      });
    }
    setShowModal(true);
    setError('');
    setFieldErrors({});
    setImageUploadError('');
  };

  const handleOpenViewModal = (ambulance: Ambulance) => {
    setViewingAmbulance(ambulance);
    setShowViewModal(true);
  };

  const handleImageFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setImageUploadError('Please select an image file');
      return;
    }

    // Validate file size (max 3MB before compression)
    const maxBytes = 3 * 1024 * 1024;
    if (file.size > maxBytes) {
      const { formatFileSizeMB } = await import('../utils/imageCompression');
      setImageUploadError(
        `The selected image (${formatFileSizeMB(file.size)}) exceeds the maximum allowed size (3 MB). Please choose a smaller file.`
      );
      return;
    }

    setImageUploadError('');
    setImageUploading(true);
    // Defer so UI shows loader immediately when user selects file
    const run = async () => {
      try {
        const { compressImage } = await import('../utils/imageCompression');
        const compressedFile = await compressImage(file, 1920, 1920, 0.8, 500);

        const uploadFormData = new FormData();
        uploadFormData.append('file', compressedFile);
        uploadFormData.append('folderPath', 'images/ambulances');

        const response = await uploadFile(uploadFormData);
        const uploadedUrl = response.data?.data?.url;
        if (uploadedUrl) {
          setFormData((prev) => ({ ...prev, image: uploadedUrl }));
        } else {
          setImageUploadError('Upload succeeded but no URL returned.');
        }
      } catch (err: any) {
        setImageUploadError(err.response?.data?.message || err.message || 'Failed to upload image');
      } finally {
        setImageUploading(false);
      }
    };
    setTimeout(run, 0);
  };

  const handleClearImage = () => {
    setFormData((prev) => ({ ...prev, image: '' }));
    setImageUploadError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formSubmitting) return;
    setError('');
    setFieldErrors({});
    setFormSubmitting(true);

    // Frontend validation for individual fields
    const newFieldErrors: typeof fieldErrors = {};

    if (!formData.name.trim()) {
      newFieldErrors.name = 'Ambulance name is required';
    }

    if (!formData.licensePlate.trim()) {
      newFieldErrors.licensePlate = 'License plate is required';
    }

    if (!formData.type) {
      newFieldErrors.type = 'Ambulance type is required';
    }

    if (
      formData.capacity &&
      (isNaN(Number(formData.capacity)) ||
        Number(formData.capacity) < 1 ||
        Number(formData.capacity) > 10)
    ) {
      newFieldErrors.capacity = 'Capacity must be a number between 1 and 10';
    }

    if (
      formData.speedLimit &&
      (isNaN(Number(formData.speedLimit)) ||
        Number(formData.speedLimit) < 0 ||
        Number(formData.speedLimit) > 200)
    ) {
      newFieldErrors.speedLimit = 'Speed limit must be between 0 and 200 km/h';
    }

    if (!formData.gpsDeviceId) {
      newFieldErrors.gpsDeviceId = 'GPS device is required';
    }

    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      setError('Please fix the highlighted errors and try again.');
      setFormSubmitting(false);
      return;
    }

    try {
      // Build pilot payload (explicit null when cleared so backend can detach)
      const pilotPayload =
        formData.assignedPilot && pilotUsers.length > 0
          ? (() => {
              const driver = pilotUsers.find(
                (u) => (u._id || u.id) === formData.assignedPilot
              );
              if (!driver) return null;
              return {
                userId: (driver._id || driver.id)!,
                name: driver.metadata?.name || driver.name,
              };
            })()
          : null;

      // Build assigned staffs payload (array)
      const assignedStaffsPayload =
        formData.assignedStaffs && formData.assignedStaffs.length > 0 && staffUsers.length > 0
          ? formData.assignedStaffs
              .map((staffId) => {
                const staff = staffUsers.find((u) => (u._id || u.id) === staffId);
                if (!staff) return null;
              return {
                  userId: (staff._id || staff.id)!,
                  name: staff.metadata?.name || staff.name,
              };
              })
              .filter((s): s is { userId: string; name: string } => s !== null)
          : [];

      // GPS device id: send null explicitly when cleared so backend detaches device
      const gpsDeviceIdPayload = formData.gpsDeviceId ? formData.gpsDeviceId : null;

      const data: any = {
        metadata: { name: formData.name.trim() },
        spec: {
          licensePlate: formData.licensePlate.trim(),
          vehicleNumber: formData.vehicleNumber.trim() || undefined,
          type: formData.type || undefined,
          status: formData.status,
          capacity: formData.capacity ? Number(formData.capacity) : undefined,
          mileage: formData.mileage ? Number(formData.mileage) : undefined,
          speedLimit: formData.speedLimit ? Number(formData.speedLimit) : undefined,
          currentLocation: formData.currentLocation || undefined,
          assignedPilot: pilotPayload,
          notes: formData.notes.trim() || undefined,
          assignedStaffs: assignedStaffsPayload.length > 0 ? assignedStaffsPayload : undefined,
          gpsDeviceId: gpsDeviceIdPayload,
          image: formData.image || undefined,
        },
      };

      if (editingAmbulance) {
        await updateAmbulance(editingAmbulance._id, data);
      } else {
        await createAmbulance(data);
      }

      setShowModal(false);
      fetchAmbulances();
    } catch (err: any) {
      const message = err.response?.data?.message || 'Operation failed';
      const newFieldErrors: typeof fieldErrors = {};
      const lower = message.toLowerCase();

      if (lower.includes('license plate')) {
        // Show a clear, consistent message on the field
        newFieldErrors.licensePlate = 'License plate must be unique';
      }
      if (lower.includes('pilot')) {
        newFieldErrors.assignedPilot = message;
      }
      if (lower.includes('staff')) {
        newFieldErrors.assignedStaffs = message;
      }
      if (lower.includes('gps')) {
        newFieldErrors.gpsDeviceId = message;
      }

      setFieldErrors(newFieldErrors);
      setError(message);
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    setAmbulanceToDelete({ id, name });
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!ambulanceToDelete) return;

    try {
      await deleteAmbulance(ambulanceToDelete.id);
      fetchAmbulances();
      setShowDeleteConfirm(false);
      setAmbulanceToDelete(null);
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || 'Failed to delete ambulance. Unknown error occurred.');
      setShowDeleteConfirm(false);
      setAmbulanceToDelete(null);
      setShowErrorModal(true);
    }
  };

  // Match ambulance status pill styling from Live Tracking
  const getStatusChipStyle = (status: string) => {
    const color =
      status === 'on_duty'
        ? '#ef4444'
        : status === 'available'
          ? '#10b981'
          : status === 'off_duty'
            ? '#f59e0b'
            : status === 'maintenance'
              ? '#64748b'
              : '#64748b';

    return {
      color,
      backgroundColor: `${color}0D`,
      border: `0.5px solid ${color}`,
    };
  };

  const formatAmbulanceStatus = (status: string): string => {
    if (!status) return 'Unknown';
    const map: Record<string, string> = {
      available: 'Available',
      on_duty: 'On Duty',
      off_duty: 'Off Duty',
      maintenance: 'Maintenance',
    };
    const lower = status.toLowerCase();
    if (map[lower]) return map[lower];
    return status
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Helper: get display name for type
  const getTypeDisplayName = (typeName?: string) => {
    if (!typeName) return '';
    const found = ambulanceTypes.find((t) => t.metadata.name === typeName);
    return found ? (found.metadata.displayName || found.metadata.name) : typeName;
  };

  // Helper: get GPS device name from gpsDeviceId/gpsDevice
  const getGpsDeviceDisplayName = (ambulance: Ambulance) => {
    // Try to get the GPS device from embedded object, else from our loaded gpsDevices state
    const gpsDeviceId = ambulance.spec.gpsDevice?.gpsDeviceId;
    if (gpsDeviceId) {
      // Try loaded list first
      const found = gpsDevices.find((d) => d._id === gpsDeviceId);
      if (found) return found.metadata?.name || gpsDeviceId;
      // Fall back to embedded deviceId or GPS deviceId
      return (
        ambulance.spec.gpsDevice?.deviceId ||
        ambulance.spec.gpsDevice?.gpsDeviceId ||
        <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>
      );
    }
    return <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>;
  };

  const content = (
    <>
      {/* Tabs - Hide all tabs when hideAmbulanceTab is true (used in Settings) */}
      {!hideAmbulanceTab && (
        <div className="ambulance-tabs-container">
          {(isSystemAdmin || isTenantAdmin || hasPermission('ambulance.mapping')) && (
            <button
              onClick={() => setActiveTab('mapping')}
              className={`ambulance-tab ${activeTab === 'mapping' ? 'active' : ''}`}
            >
              Staff Mapping
            </button>
          )}
          {(isSystemAdmin || isTenantAdmin || hasPermission('area.update')) && (
            <button
              onClick={() => setActiveTab('areaMapping')}
              className={`ambulance-tab ${activeTab === 'areaMapping' ? 'active' : ''}`}
            >
              Area Mapping
            </button>
          )}
          <button
            onClick={() => setActiveTab('history')}
            className={`ambulance-tab ${activeTab === 'history' ? 'active' : ''}`}
          >
            History
          </button>
        </div>
      )}

      {(activeTab === 'ambulances' || hideAmbulanceTab) && (
      <div className="card">
        <div className="card-header">
          {/* <h3 className="card-title">All Ambulances</h3> */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Search by name, license plate, or vehicle number..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="search-input"
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontSize: '16px',
                  width: '220px',
                  flex: '0 0 220px',
                }}
              />
              {searchTerm && (
                <button
                  onClick={handleClearSearch}
                  style={{
                    padding: '8px 16px',
                    background: '#999',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '16px',
                  }}
                >
                  Clear
                </button>
              )}
              {debouncedSearchTerm && debouncedSearchTerm !== searchTerm && (
                <span style={{ fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                  Searching...
                </span>
              )}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid',
                  borderColor: isDark ? '#334155' : '#ddd',
                  borderRadius: '6px',
                  fontSize: '16px',
                  cursor: 'pointer',
                }}
              >
                <option value="">All Status</option>
                <option value="available">Available</option>
                <option value="on_duty">On Duty</option>
                <option value="off_duty">Off Duty</option>
                <option value="maintenance">Maintenance</option>
              </select>
            </div>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="refresh-button"
              title="Refresh list"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
              {loading ? 'Loading...' : 'Refresh'}
            </button>
            {hasPermission('ambulance.create') && (
              <button className="btn-add" onClick={() => handleOpenModal()}>
                + Add Ambulance
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading ambulances...</div>
        ) : ambulances.length === 0 ? (
          <div className="empty-state">
            <h3>No ambulances yet</h3>
            <p>Create your first ambulance to get started</p>
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
          <div className="table-container" style={{ height: 'calc(100vh - 450px)', overflow: 'auto' }} onScroll={() => setStaffTooltip(null)}>
            <table className="bookings-table">
              <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                <tr>
                  <th>Name</th>
                  <th>License Plate</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Capacity</th>
                  <th>Mileage (Km/l)</th>
                  <th>Speed Limit (Km/h)</th>
                  <th>Pilot</th>
                  <th>Staff</th>
                  <th>GPS Device</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {ambulances.map((ambulance) => {
                  const statusChipStyle = getStatusChipStyle(ambulance.spec.status);
                  return (
                    <tr key={ambulance._id}>
                      <td>
                        <strong 
                          title={ambulance.metadata.name}
                          style={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '200px',
                          }}
                        >
                          {ambulance.metadata.name.length > 25 
                            ? `${ambulance.metadata.name.substring(0, 25)}...` 
                            : ambulance.metadata.name}
                        </strong>
                      </td>
                      <td>
                        <code>
                          {ambulance.spec.licensePlate}
                        </code>
                      </td>
                      <td>
                        {/* Show type displayName if found, else fallback to name/type */}
                        {ambulance.spec.type ? (
                          (() => {
                            const typeDisplayName = getTypeDisplayName(ambulance.spec.type);
                            return (
                              <span
                                title={typeDisplayName}
                                style={{
                                  display: 'block',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: '160px',
                                }}
                              >
                                {typeDisplayName.length > 20 
                                  ? `${typeDisplayName.substring(0, 20)}...` 
                                  : typeDisplayName}
                              </span>
                            );
                          })()
                        ) : (
                          <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>
                        )}
                      </td>
                      <td>
                        <span
                          style={{
                            fontSize: '14px',
                            padding: '3px 6px',
                            borderRadius: '4px',
                            fontWeight: '500',
                            display: 'inline-block',
                            ...statusChipStyle,
                          }}
                        >
                          {formatAmbulanceStatus(ambulance.spec.status)}
                        </span>
                      </td>
                      <td>{ambulance.spec.capacity || <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>}</td>
                      <td>
                        {ambulance.spec.mileage !== undefined && ambulance.spec.mileage !== null
                          ? `${ambulance.spec.mileage} km/l`
                          : <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>
                        }
                      </td>
                      <td>
                        {ambulance.spec.speedLimit !== undefined && ambulance.spec.speedLimit !== null
                          ? `${ambulance.spec.speedLimit} km/h`
                          : <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>
                        }
                      </td>
                      <td>
                        {ambulance.spec.assignedPilot?.name || (
                          <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>
                        )}
                      </td>
                      <td>
                        {ambulance.spec.assignedStaffs && ambulance.spec.assignedStaffs.length > 0 ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span style={{ fontSize: '15px' }}>
                              {ambulance.spec.assignedStaffs[0].name}
                            </span>
                            {ambulance.spec.assignedStaffs.length > 1 && (
                              <span
                                onMouseEnter={(e) => {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setStaffTooltip({
                                    x: rect.left + rect.width / 2,
                                    y: rect.top - 5,
                                    staffList: ambulance.spec.assignedStaffs || [],
                                  });
                                }}
                                onMouseLeave={() => setStaffTooltip(null)}
                                style={{
                                  cursor: 'help',
                                  color: isDark ? '#60a5fa' : '#3b82f6',
                                  fontSize: '16px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                }}
                              >
                                ℹ️
                              </span>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>
                        )}
                      </td>
                      <td>
                        {getGpsDeviceDisplayName(ambulance)}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDateDDMMYYYY(ambulance.metadata.createdAt)}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-icon btn-view"
                            onClick={() => handleOpenViewModal(ambulance)}
                          >
                            View
                          </button>
                          {hasPermission('ambulance.update') && (
                            <button className="btn-icon btn-edit" onClick={() => handleOpenModal(ambulance)}>
                              Edit
                            </button>
                          )}
                          {hasPermission('ambulance.delete') && (
                            <button
                              className="btn-icon btn-delete"
                              onClick={() => handleDelete(ambulance._id, ambulance.metadata.name)}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>

            {staffTooltip && (
              <div
                style={{
                  position: 'fixed',
                  left: `${staffTooltip.x}px`,
                  top: `${staffTooltip.y}px`,
                  transform: 'translate(-50%, -100%)',
                  backgroundColor: isDark ? '#1e293b' : '#0f172a',
                  color: '#ffffff',
                  padding: '10px 14px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  zIndex: 10000,
                  pointerEvents: 'none',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  maxWidth: '300px',
                  marginBottom: '5px',
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: '6px', fontSize: '15px' }}>All Staff:</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {staffTooltip.staffList.map((staff, idx) => (
                    <div key={idx} style={{ fontSize: '14px' }}>
                      {staff.name}
                    </div>
                  ))}
                </div>
                <div
                  style={{
                    position: 'absolute',
                    bottom: '-5px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 0,
                    height: 0,
                    borderLeft: '5px solid transparent',
                    borderRight: '5px solid transparent',
                    borderTop: `5px solid ${isDark ? '#1e293b' : '#0f172a'}`,
                  }}
                />
              </div>
            )}

            <div className="pagination-controls" style={{ position: 'relative', width: '100%' }}>
              <div className="pagination-left" style={{ position: 'absolute', right: 250, whiteSpace: 'nowrap', zIndex: 10 }}>
                <label style={{ fontSize: '16px', whiteSpace: 'nowrap' }}>
                  Rows per page:{' '}
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    style={{ minWidth: '60px', width: 'auto' }}
                  >
                    {[10, 25, 50, 100].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="pagination-center">
                <span className="pagination-info">
                  {totalItems === 0
                    ? '0 of 0'
                    : `${(currentPage - 1) * pageSize + 1}–${Math.min(
                        currentPage * pageSize,
                        totalItems
                      )} of ${totalItems}`}
                </span>
              </div>
              <div className="pagination-right">
                <button
                  className="btn-secondary"
                  disabled={!hasPreviousPage || loading}
                  onClick={() => hasPreviousPage && handlePageChange(currentPage - 1)}
                >
                  ‹
                </button>
                <button
                  className="btn-secondary"
                  disabled={!hasNextPage || loading}
                  onClick={() => hasNextPage && handlePageChange(currentPage + 1)}
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {!hideAmbulanceTab && activeTab === 'history' && (
        <div className="card" style={{ height: 'calc(100vh - 215px)', marginTop: '0px' }}>
          <AmbulanceAssignmentHistoryContent />
        </div>
      )}

      {!hideAmbulanceTab && activeTab === 'mapping' && canViewMapping && (
        <AmbulanceStaffMapping
          ambulances={allAmbulances}
          loading={loading}
          getStatusChipStyle={getStatusChipStyle}
          formatAmbulanceStatus={formatAmbulanceStatus}
          pilots={pilotUsers}
          otherStaff={staffUsers}
          ambulanceTypes={ambulanceTypes}
          staffCategories={staffCategories}
          onUpdate={async (ambulanceId, pilotId, staffIds) => {
            try {
              // Find the selected pilot and staff details
              const pilot = pilotId ? pilotUsers.find((p) => (p._id || p.id) === pilotId) : null;
              const staffs = staffIds
                .map((id) => staffUsers.find((s) => (s._id || s.id) === id))
                .filter((s): s is User => s !== undefined);

              const data: any = {
                spec: {
                  assignedPilot: pilot
                    ? {
                        userId: (pilot._id || pilot.id)!,
                        name: pilot.metadata?.name || pilot.name,
                      }
                    : null,
                  assignedStaffs: staffs.length > 0
                    ? staffs.map((s) => ({
                        userId: (s._id || s.id)!,
                        name: s.metadata?.name || s.name,
                      }))
                    : undefined,
                },
              };

              await updateAmbulance(ambulanceId, data);
              // Refresh the list used by Staff Mapping so UI shows new assignments when switching ambulances
              await fetchAllAmbulances();
              fetchAmbulances();
            } catch (err: any) {
              throw err; // Re-throw to let component handle it
            }
          }}
        />
      )}

      {!hideAmbulanceTab && activeTab === 'areaMapping' && canViewAreaMapping && (
        <AreaMapping
          areas={areas}
          ambulances={allAmbulances}
          loading={areasLoading || loading}
          getStatusChipStyle={getStatusChipStyle}
          formatAmbulanceStatus={formatAmbulanceStatus}
          onUpdate={async (areaId, ambulanceIds) => {
            try {
              const data: any = {
                spec: {
                  ambulances: ambulanceIds,
                },
              };

              await updateArea(areaId, data);
              await fetchAreas(); // Refresh the areas list
            } catch (err: any) {
              throw err; // Re-throw to let component handle it
            }
          }}
        />
      )}

      {showViewModal && viewingAmbulance && (
        <div
          className="drawer-overlay"
          onClick={() => {
            setShowViewModal(false);
            setViewingAmbulance(null);
          }}
        >
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3 className="drawer-title">View Ambulance</h3>
              <button
                className="drawer-close"
                onClick={() => {
                  setShowViewModal(false);
                  setViewingAmbulance(null);
                }}
              >
                ×
              </button>
            </div>

            <div className="drawer-body" style={{ padding: '24px', overflowY: 'auto', maxHeight: 'calc(100vh - 120px)' }}>
              {(() => {
                const viewBoxStyle: React.CSSProperties = {
                  padding: '10px',
                  border: isDark ? '1px solid #475569' : '1px solid #ddd',
                  borderRadius: '6px',
                  backgroundColor: isDark ? '#1e293b' : '#f8f9fa',
                  color: isDark ? '#f1f5f9' : '#000',
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center',
                };
                const dash = <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>;
                const spec = viewingAmbulance.spec;
                const staffList = spec.assignedStaffs || [];
                const locationText = spec.currentLocation
                  ? spec.currentLocation.address
                    || `${spec.currentLocation.latitude.toFixed(6)}, ${spec.currentLocation.longitude.toFixed(6)}`
                  : null;
                const ambulanceImage = spec.image;

                return (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div className="form-group">
                        <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Name</label>
                        <div style={viewBoxStyle}>{viewingAmbulance.metadata.name}</div>
                      </div>
                      <div className="form-group">
                        <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>License Plate</label>
                        <div style={viewBoxStyle}><code>{spec.licensePlate}</code></div>
                      </div>
                      <div className="form-group">
                        <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Vehicle Number</label>
                        <div style={viewBoxStyle}>{spec.vehicleNumber || dash}</div>
                      </div>
                      <div className="form-group">
                        <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Type</label>
                        <div style={viewBoxStyle}>{spec.type ? getTypeDisplayName(spec.type) : dash}</div>
                      </div>
                      <div className="form-group">
                        <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Status</label>
                        <div style={viewBoxStyle}>
                          <span
                            style={{
                              fontSize: '14px',
                              padding: '3px 6px',
                              borderRadius: '4px',
                              fontWeight: 500,
                              display: 'inline-block',
                              ...getStatusChipStyle(spec.status),
                            }}
                          >
                            {formatAmbulanceStatus(spec.status)}
                          </span>
                        </div>
                      </div>
                      <div className="form-group">
                        <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>GPS Device</label>
                        <div style={viewBoxStyle}>{getGpsDeviceDisplayName(viewingAmbulance)}</div>
                      </div>
                      <div className="form-group">
                        <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Capacity</label>
                        <div style={viewBoxStyle}>{spec.capacity ?? dash}</div>
                      </div>
                      <div className="form-group">
                        <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Mileage (Km/l)</label>
                        <div style={viewBoxStyle}>
                          {spec.mileage !== undefined && spec.mileage !== null ? `${spec.mileage} km/l` : dash}
                        </div>
                      </div>
                      <div className="form-group">
                        <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Speed Limit (Km/h)</label>
                        <div style={viewBoxStyle}>
                          {spec.speedLimit !== undefined && spec.speedLimit !== null ? `${spec.speedLimit} km/h` : dash}
                        </div>
                      </div>
                      <div className="form-group">
                        <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Assigned Pilot</label>
                        <div style={viewBoxStyle}>{spec.assignedPilot?.name || dash}</div>
                      </div>
                      <div className="form-group">
                        <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Assigned Staff</label>
                        <div style={{ ...viewBoxStyle, alignItems: staffList.length > 1 ? 'flex-start' : 'center', flexDirection: 'column', gap: '4px' }}>
                          {staffList.length > 0
                            ? staffList.map((staff) => <div key={staff.userId}>{staff.name}</div>)
                            : dash}
                        </div>
                      </div>
                      <div className="form-group">
                        <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Created</label>
                        <div style={viewBoxStyle}>{formatDateDDMMYYYY(viewingAmbulance.metadata.createdAt)}</div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Current Location</label>
                      <div style={{ ...viewBoxStyle, minHeight: '40px' }}>{locationText || dash}</div>
                    </div>

                    <div className="form-group">
                      <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Ambulance Image</label>
                      {ambulanceImage ? (
                        <img
                          src={ambulanceImage}
                          alt={viewingAmbulance.metadata.name}
                          style={{
                            width: '100%',
                            maxWidth: '300px',
                            height: '200px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                          }}
                        />
                      ) : (
                        <div style={viewBoxStyle}>{dash}</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Notes</label>
                      <div style={{ ...viewBoxStyle, minHeight: '60px', whiteSpace: 'pre-wrap', alignItems: 'flex-start' }}>
                        {spec.notes || dash}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>

            <div className="drawer-footer">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => {
                  setShowViewModal(false);
                  setViewingAmbulance(null);
                }}
              >
                Close
              </button>
              {hasPermission('ambulance.update') && (
                <button
                  type="button"
                  className="btn-submit"
                  onClick={() => {
                    const ambulance = viewingAmbulance;
                    setShowViewModal(false);
                    setViewingAmbulance(null);
                    handleOpenModal(ambulance);
                  }}
                >
                  Edit Ambulance
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="drawer-overlay" onClick={() => setShowModal(false)}>
          <div
            className="drawer"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="drawer-header">
              <h3 className="drawer-title">{editingAmbulance ? 'Edit Ambulance' : 'Create New Ambulance'}</h3>
              <button className="drawer-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="drawer-form">
              <div className="drawer-body">
                {error && <div className="error-message">{error}</div>}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="form-group">
                    <label>
                      Name <span className="required-star">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Ambulance name"
                      className={fieldErrors.name ? 'input-error' : ''}
                    />
                    {fieldErrors.name && (
                      <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                        {fieldErrors.name}
                      </small>
                    )}
                  </div>

                  <div className="form-group">
                    <label>
                      License Plate <span className="required-star">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.licensePlate}
                      onChange={(e) => setFormData({ ...formData, licensePlate: e.target.value.toUpperCase() })}
                      placeholder="License plate number"
                      style={{ textTransform: 'uppercase' }}
                      className={fieldErrors.licensePlate ? 'input-error' : ''}
                    />
                    {fieldErrors.licensePlate && (
                      <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                        {fieldErrors.licensePlate}
                      </small>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Vehicle Number</label>
                    <input
                      type="text"
                      value={formData.vehicleNumber}
                      onChange={(e) => setFormData({ ...formData, vehicleNumber: e.target.value })}
                      placeholder="Vehicle number"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Type <span className="required-star">*</span>
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      style={{ 
                        width: '100%', 
                        padding: '10px', 
                        border: isDark ? '1px solid #334155' : '1px solid #ddd', 
                        borderRadius: '6px' 
                      }}
                      className={fieldErrors.type ? 'input-error' : ''}
                    >
                      <option value="">Select type</option>
                      {ambulanceTypes.map((type) => {
                        const isSystemLevel = type.spec?.tenantId === null || type.spec?.tenantId === undefined;
                        return (
                          <option key={type._id} value={type.metadata.name}>
                            {type.metadata.displayName || type.metadata.name}
                            {isSystemLevel ? ' 🌍 (System)' : ''}
                          </option>
                        );
                      })}
                    </select>
                    {ambulanceTypes.length === 0 && (
                      <small style={{ color: '#666', fontSize: '14px', display: 'block', marginTop: '4px' }}>
                        No ambulance types available. Create types in Ambulance Type Management.
                      </small>
                    )}
                    {fieldErrors.type && (
                      <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                        {fieldErrors.type}
                      </small>
                    )}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 0, marginBottom: 8 }}>
                  <div className="form-group">
                    <label>
                      Status <span className="required-star">*</span>
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      required
                      style={{ 
                        width: '100%', 
                        padding: '10px', 
                        border: isDark ? '1px solid #334155' : '1px solid #ddd', 
                        borderRadius: '6px' 
                      }}
                    >
                      <option value="available">Available</option>
                      <option value="on_duty">On Duty</option>
                      <option value="off_duty">Off Duty</option>
                      <option value="maintenance">Maintenance</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>
                      GPS Device ID <span className="required-star">*</span>
                    </label>
                    <select
                      value={formData.gpsDeviceId}
                      onChange={(e) => setFormData({ ...formData, gpsDeviceId: e.target.value })}
                      style={{ 
                        width: '100%', 
                        padding: '10px', 
                        border: isDark ? '1px solid #334155' : '1px solid #ddd', 
                        borderRadius: '6px' 
                      }}
                      className={fieldErrors.gpsDeviceId ? 'input-error' : ''}
                    >
                      <option value="">No GPS device</option>
                      {gpsDevices.map((device) => (
                        <option key={device._id} value={device._id}>
                          {device.metadata.name}
                        </option>
                      ))}
                    </select>
                    <small style={{ color: '#666', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      Each GPS device can be attached to only one ambulance at a time.
                    </small>
                    {fieldErrors.gpsDeviceId && (
                      <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                        {fieldErrors.gpsDeviceId}
                      </small>
                    )}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginTop: 0, marginBottom: 8 }}>
                  <div className="form-group">
                    <label>Capacity</label>
                    <input
                      type="number"
                      value={formData.capacity}
                      onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                      min="1"
                      max="10"
                      placeholder="Number of patients"
                      className={fieldErrors.capacity ? 'input-error' : ''}
                    />
                    {fieldErrors.capacity && (
                      <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                        {fieldErrors.capacity}
                      </small>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Mileage (Km/l)</label>
                    <input
                      type="number"
                      value={formData.mileage}
                      onChange={(e) => setFormData({ ...formData, mileage: e.target.value })}
                      min="0"
                      step="0.1"
                      placeholder="Kilometers per liter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Speed Limit (Km/h)</label>
                    <input
                      type="number"
                      value={formData.speedLimit}
                      onChange={(e) => setFormData({ ...formData, speedLimit: e.target.value })}
                      min="0"
                      max="200"
                      step="1"
                      placeholder="e.g. 80"
                      className={fieldErrors.speedLimit ? 'input-error' : ''}
                    />
                    {fieldErrors.speedLimit && (
                      <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                        {fieldErrors.speedLimit}
                      </small>
                    )}
                  </div>
                </div>

                {/* Assigned Pilot and Assigned Staffs - Side by Side */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 0, marginBottom: 8 }}>
                  <div className="form-group">
                    <label>
                      Assigned Pilot
                    </label>
                  <div ref={pilotDropdownRef} style={{ position: 'relative', width: '100%', zIndex: showPilotDropdown ? 10001 : 'auto' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowPilotDropdown(!showPilotDropdown);
                        if (!showPilotDropdown) {
                          setPilotSearchTerm('');
                        }
                      }}
                      className="custom-select-button"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: fieldErrors.assignedPilot ? '1px solid #e74c3c' : '1px solid',
                        borderRadius: '6px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '16px',
                      }}
                    >
                      <span>
                        {(() => {
                          const selectedPilot = pilotUsers.find((s) => (s._id || s.id) === formData.assignedPilot);
                          return selectedPilot
                            ? selectedPilot.metadata?.name || selectedPilot.name
                            : 'Select a pilot';
                        })()}
                      </span>
                      <span style={{ fontSize: '14px' }}>{showPilotDropdown ? '▲' : '▼'}</span>
                    </button>
                    {showPilotDropdown && (
                      <div
                        className="custom-dropdown-menu"
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          marginTop: '4px',
                          borderRadius: '6px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          zIndex: 10000,
                          maxHeight: '300px',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                          isolation: 'isolate',
                        }}
                      >
                        <input
                          type="text"
                          placeholder="Search pilot by name, phone, or role..."
                          value={pilotSearchTerm}
                          onChange={(e) => setPilotSearchTerm(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="search-input"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: 'none',
                            borderBottom: '1px solid',
                            borderRadius: '6px 6px 0 0',
                            fontSize: '16px',
                            outline: 'none',
                          }}
                          autoFocus
                        />
                        <div
                          style={{
                            maxHeight: '250px',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                          }}
                        >
                          {(() => {
                            const searchLower = pilotSearchTerm.toLowerCase().trim();
                            
                            const filteredPilots = pilotUsers.filter((pilot) => {
                              // Filter by search term (name, phone, role)
                              if (searchLower !== '') {
                                const name = (pilot.metadata?.name || pilot.name || '').toLowerCase();
                                const phone = (pilot.spec?.phone || pilot.phone || '').toLowerCase();
                                const roleStr = (pilot.spec?.roles || pilot.roles || []).join(' ').toLowerCase();
                                
                                if (!name.includes(searchLower) && 
                                    !phone.includes(searchLower) && 
                                    !roleStr.includes(searchLower)) {
                                  return false;
                                }
                              }
                              return true;
                            });

                            if (filteredPilots.length === 0) {
                          return (
                                <div
                                  style={{
                                    padding: '12px',
                                    textAlign: 'center',
                                    color: '#666',
                                    fontSize: '16px',
                                  }}
                                >
                                  No pilots found
                                </div>
                              );
                            }

                            return filteredPilots.map((pilot) => {
                              const handleSelect = () => {
                                const val = pilot._id || pilot.id || '';
                                setFormData({ ...formData, assignedPilot: val });
                                setShowPilotDropdown(false);
                                setPilotSearchTerm('');
                              };

                              const isSelected = formData.assignedPilot === (pilot._id || pilot.id);
                              const name = pilot.metadata?.name || pilot.name || '';
                              const phone = pilot.spec?.phone || pilot.phone || '';
                              const roles = (pilot.spec?.roles || pilot.roles || []);

                              return (
                                <div
                                  key={pilot._id || pilot.id}
                                  onClick={handleSelect}
                                  className={`custom-dropdown-option ${isSelected ? 'selected' : ''}`}
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f0f0f0',
                                    fontSize: '16px',
                                    transition: 'background-color 0.2s',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isSelected) {
                                      e.currentTarget.style.backgroundColor = '';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isSelected) {
                                      e.currentTarget.style.backgroundColor = '';
                                    }
                                  }}
                                >
                                  <div style={{ fontWeight: isSelected ? '600' : 'normal' }}>
                                    {name}
                                    {phone && (
                                      <span style={{ color: '#666', fontSize: '14px', marginLeft: '8px' }}>
                                        {phone}
                                      </span>
                                    )}
                                    {roles && roles.length > 0 && (
                                      <div style={{ color: '#999', fontSize: '14px', marginTop: '2px' }}>
                                        {formatRoles(roles)}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                    {pilotUsers.length === 0 && (
                      <small style={{ color: '#666', fontSize: '14px', display: 'block', marginTop: '4px' }}>
                        No pilot users found. Ensure users with staff category "Pilot" are created.
                      </small>
                    )}
                    {fieldErrors.assignedPilot && (
                      <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                        {fieldErrors.assignedPilot}
                      </small>
                    )}
                  </div>

                  <div className="form-group">
                    <label>
                    Assigned Staffs
                    </label>
                  <div ref={staffDropdownRef} style={{ position: 'relative', width: '100%', zIndex: showStaffDropdown ? 10001 : 'auto' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowStaffDropdown(!showStaffDropdown);
                        if (!showStaffDropdown) {
                          setStaffSearchTerm('');
                        }
                      }}
                      className="custom-select-button"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: fieldErrors.assignedStaffs ? '1px solid #e74c3c' : '1px solid',
                        borderRadius: '6px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '16px',
                      }}
                    >
                      <span>
                        {(() => {
                          const selectedCount = formData.assignedStaffs.length;
                          if (selectedCount === 0) {
                            return 'Select staff members';
                          }
                          if (selectedCount === 1) {
                            const selectedStaff = staffUsers.find((s) => (s._id || s.id) === formData.assignedStaffs[0]);
                            return selectedStaff ? (selectedStaff.metadata?.name || selectedStaff.name) : '1 staff selected';
                          }
                          return `${selectedCount} staff members selected`;
                        })()}
                      </span>
                      <span style={{ fontSize: '14px' }}>{showStaffDropdown ? '▲' : '▼'}</span>
                    </button>
                    {showStaffDropdown && (
                      <div
                        className="custom-dropdown-menu"
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          marginTop: '4px',
                          borderRadius: '6px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          zIndex: 10000,
                          maxHeight: '300px',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                          isolation: 'isolate',
                        }}
                      >
                        <input
                          type="text"
                          placeholder="Search staff by name, phone, or role..."
                          value={staffSearchTerm}
                          onChange={(e) => setStaffSearchTerm(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="search-input"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: 'none',
                            borderBottom: '1px solid',
                            borderRadius: '6px 6px 0 0',
                            fontSize: '16px',
                            outline: 'none',
                          }}
                          autoFocus
                        />
                        <div
                          style={{
                            maxHeight: '250px',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                          }}
                        >
                          {(() => {
                            const searchLower = staffSearchTerm.toLowerCase().trim();
                            
                            const filteredStaff = staffUsers.filter((staff) => {
                              // Filter by search term (name, phone, role)
                              if (searchLower !== '') {
                                const name = (staff.metadata?.name || staff.name || '').toLowerCase();
                                const phone = (staff.spec?.phone || staff.phone || '').toLowerCase();
                                const roleStr = (staff.spec?.roles || staff.roles || []).join(' ').toLowerCase();
                                
                                if (!name.includes(searchLower) && 
                                    !phone.includes(searchLower) && 
                                    !roleStr.includes(searchLower)) {
                                  return false;
                                }
                              }
                              return true;
                            });

                            if (filteredStaff.length === 0) {
                          return (
                                <div
                                  style={{
                                    padding: '12px',
                                    textAlign: 'center',
                                    color: '#666',
                                    fontSize: '16px',
                                  }}
                                >
                                  No staff found
                                </div>
                              );
                            }

                            return filteredStaff.map((staff) => {
                              const staffId = staff._id || staff.id || '';
                              const isSelected = formData.assignedStaffs.includes(staffId);
                              const name = staff.metadata?.name || staff.name || '';
                              const phone = staff.spec?.phone || staff.phone || '';
                              const roles = (staff.spec?.roles || staff.roles || []);

                              const handleToggle = () => {
                                if (isSelected) {
                                  setFormData({
                                    ...formData,
                                    assignedStaffs: formData.assignedStaffs.filter((id) => id !== staffId),
                                  });
                                } else {
                                  setFormData({
                                    ...formData,
                                    assignedStaffs: [...formData.assignedStaffs, staffId],
                                  });
                                }
                              };

                              return (
                                <div
                                  key={staffId}
                                  onClick={handleToggle}
                                  className={`custom-dropdown-option ${isSelected ? 'selected' : ''}`}
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f0f0f0',
                                    fontSize: '16px',
                                    transition: 'background-color 0.2s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isSelected) {
                                      e.currentTarget.style.backgroundColor = '';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isSelected) {
                                      e.currentTarget.style.backgroundColor = '';
                                    }
                                  }}
                                >
                                  <div style={{ position: 'relative', flexShrink: 0 }}>
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={(e) => e.stopPropagation()}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggle();
                                      }}
                                      className="staff-checkbox"
                                      style={{
                                        width: '18px',
                                        height: '18px',
                                        borderRadius: '50%',
                                        cursor: 'pointer',
                                        appearance: 'none',
                                        border: isSelected ? '2px solid #667eea' : undefined,
                                        backgroundColor: isSelected ? '#667eea' : undefined,
                                        position: 'relative',
                                        margin: 0,
                                        padding: 0,
                                      }}
                                    />
                                    {isSelected && (
                                      <span
                                        style={{
                                          position: 'absolute',
                                          top: '50%',
                                          left: '50%',
                                          transform: 'translate(-50%, -50%)',
                                          color: '#fff',
                                          fontSize: '12px',
                                          fontWeight: 'bold',
                                          pointerEvents: 'none',
                                          lineHeight: 1,
                                        }}
                                      >
                                        ✓
                                      </span>
                                    )}
                                  </div>
                                  <div style={{ flex: 1, fontWeight: isSelected ? '600' : 'normal' }}>
                                    {name}
                                    {phone && (
                                      <span style={{ color: '#666', fontSize: '14px', marginLeft: '8px' }}>
                                        {phone}
                                      </span>
                                    )}
                                    {roles && roles.length > 0 && (
                                      <div style={{ color: '#999', fontSize: '14px', marginTop: '2px' }}>
                                        {formatRoles(roles)}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                  {staffUsers.length === 0 && (
                      <small style={{ color: '#666', fontSize: '14px', display: 'block', marginTop: '4px' }}>
                      No staff users found. Ensure users (excluding pilots/drivers) are created.
                      </small>
                    )}
                  {fieldErrors.assignedStaffs && (
                      <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {fieldErrors.assignedStaffs}
                      </small>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Current Location</label>
                  <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                    Search for a place or click on the map to set the ambulance location
                  </p>
                  <PlaceSearch
                    onPlaceSelect={(lat, lng, address) => {
                      setFormData((prev) => ({
                        ...prev,
                        currentLocation: { latitude: lat, longitude: lng, address },
                      }));
                    }}
                    placeholder="Search for a location..."
                    currentLocation={formData.currentLocation}
                  />
                  <GoogleMap
                    center={
                      formData.currentLocation 
                        ? { lat: formData.currentLocation.latitude, lng: formData.currentLocation.longitude }
                        : hospitalLocation
                        ? { lat: hospitalLocation.latitude, lng: hospitalLocation.longitude }
                        : { lat: 20.5937, lng: 78.9629 } // Center of India for full map view
                    }
                    zoom={
                      formData.currentLocation 
                        ? 13 
                        : hospitalLocation 
                        ? 13 
                        : 5 // Lower zoom for full India map
                    }
                    location={formData.currentLocation}
                    onLocationChange={(lat, lng) => {
                      setFormData((prev) => ({
                        ...prev,
                        currentLocation: { latitude: lat, longitude: lng },
                      }));
                    }}
                    height="300px"
                    />
                  {formData.currentLocation && (
                    <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                      <strong>Selected:</strong> {formData.currentLocation.latitude.toFixed(6)}, {formData.currentLocation.longitude.toFixed(6)}
                      {formData.currentLocation.address && ` - ${formData.currentLocation.address}`}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      // Get user's current location
                      if (navigator.geolocation) {
                        navigator.geolocation.getCurrentPosition(
                          (position) => {
                            setFormData((prev) => ({
                              ...prev,
                              currentLocation: {
                                latitude: position.coords.latitude,
                                longitude: position.coords.longitude,
                              },
                            }));
                          },
                          (error) => {
                            console.error('Error getting location:', error);
                            setError('Unable to get your current location');
                          }
                        );
                      } else {
                        setError('Geolocation is not supported by your browser');
                      }
                    }}
                    style={{
                      marginTop: '8px',
                      padding: '6px 12px',
                      background: '#667eea',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    Use My Current Location
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setFormData({ ...formData, currentLocation: null });
                    }}
                    style={{
                      marginTop: '8px',
                      marginLeft: '8px',
                      padding: '6px 12px',
                      background: '#f44336',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    Clear Location
                  </button>
                </div>
                {/* -------------------------------------- */}
                <div className="form-group">
                  <label>Ambulance Image</label>
                  <div className="avatar-upload-row">
                    <input
                      type="url"
                      value={formData.image}
                      onChange={(e) =>
                        setFormData({ ...formData, image: e.target.value })
                      }
                      placeholder="https://example.com/ambulance.jpg"
                      style={{ flex: '1 1 260px' }}
                    />
                    {!formData.image && (
                      <label className={`avatar-upload-button ${imageUploading ? 'disabled' : ''}`} style={{ marginBottom: 0 }}>
                        <input
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={handleImageFileChange}
                          disabled={imageUploading}
                        />
                        {imageUploading ? (
                          <>
                            <span className="loading-spinner btn-spinner" aria-hidden style={{ marginRight: '8px' }} />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <span role="img" aria-label="upload" style={{ marginRight: '8px', display: 'inline-block' }}>
                              📤
                            </span>
                            Upload
                          </>
                        )}
                      </label>
                    )}
                    {formData.image && (
                      <button type="button" onClick={handleClearImage} className="btn-secondary avatar-clear-button">
                        Clear
                      </button>
                    )}
                  </div>
                  {imageUploading && (
                    <small style={{ color: '#2563eb', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      Processing & uploading image...
                    </small>
                  )}
                  {imageUploadError && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {imageUploadError}
                    </small>
                  )}
                  <small style={{ color: '#666', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                    Upload an image or paste a URL for the ambulance image. <span style={{ fontWeight: 600, color: '#374151' }}>Max size: 3MB.</span>
                  </small>
                  {formData.image && (
                    <div style={{ marginTop: '12px' }}>
                      <img
                        src={formData.image}
                        alt="Ambulance preview"
                        style={{
                          width: '100%',
                          maxWidth: '300px',
                          height: '200px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>
                {/* -------------------------------------- */}
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes"
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: isDark ? '1px solid #334155' : '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '16px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                    }}
                  />
                </div>
              </div>

              <div className="drawer-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)} disabled={formSubmitting}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={formSubmitting}>
                  {formSubmitting ? (
                    <>
                      <span className="loading-spinner btn-spinner" aria-hidden />
                      {editingAmbulance ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    `${editingAmbulance ? 'Update' : 'Create'} Ambulance`
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Ambulance"
        message={`Are you sure you want to delete the ambulance "${ambulanceToDelete?.name}"? This action cannot be undone.`}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setAmbulanceToDelete(null);
        }}
      />

      <ConfirmModal
        isOpen={showErrorModal}
        title="Error"
        message={errorMessage}
        confirmText="OK"
        cancelText=""
        type="danger"
        onConfirm={() => setShowErrorModal(false)}
        onCancel={() => setShowErrorModal(false)}
      />
    </>
  );

  if (skipLayout) {
    return content;
  }

  return <DashboardLayout>{content}</DashboardLayout>;
};

export default AmbulanceManagement;
